/**
 * Peer manager - handles multiple peer connections
 * Includes signal relay capability - peers can relay signals for newcomers
 */

import { PeerConnection, ConnectionState, PeerConnectionEvents } from './webrtc.js';

export interface PeerStats {
  peerId: string;
  state: ConnectionState;
  channels: string[];
  bytesReceived: number;
  bytesSent: number;
}

export interface SignalHandler {
  sendOffer(to: string, sdp: string): void;
  sendAnswer(to: string, sdp: string): void;
  sendIceCandidate(to: string, candidate: RTCIceCandidate): void;
}

export interface PeerManagerEvents {
  onPeerConnected?: (peerId: string) => void;
  onPeerDisconnected?: (peerId: string) => void;
  onChannelOpen?: (peerId: string, channel: string) => void;
  onMessage?: (peerId: string, channel: string, data: ArrayBuffer | string) => void;
  onRelayRequest?: (fromPeer: string, signal: RelaySignal) => void;
  onCallSignal?: (fromPeer: string, signal: CallSignal) => void;
}

export interface PeerManagerOptions {
  maxPeers?: number;
}

/**
 * Signal relay message - forwarded through connected peers
 */
export interface RelaySignal {
  type: 'relay-offer' | 'relay-answer' | 'relay-ice';
  from: string;      // Original sender
  to: string;        // Target peer
  payload: string;   // SDP or ICE candidate JSON
  hops: number;      // Number of relay hops (prevent infinite loops)
}

const MAX_RELAY_HOPS = 3;
const RELAY_CHANNEL = 'signal-relay';
const KEEPALIVE_INTERVAL_MS = 30000;  // Send ping every 30s
const KEEPALIVE_TIMEOUT_MS = 45000;   // Consider dead if no activity for 45s
const DEFAULT_MAX_PEERS = 10;         // Default max peer connections

/** Channel name for keepalive ping/pong messages */
export const KEEPALIVE_CHANNEL = 'keepalive';

/** Channel name for call signaling (WebRTC SDP/ICE for media) */
export const CALL_CHANNEL = 'call-signaling';

/**
 * Call signal message - used for WebRTC media connection negotiation
 */
export interface CallSignal {
  type: 'call-sdp-offer' | 'call-sdp-answer' | 'call-ice';
  callId: string;
  payload: string;  // SDP or ICE candidate JSON
}

/**
 * Manages multiple WebRTC peer connections
 */
export class PeerManager {
  private peers: Map<string, PeerConnection> = new Map();
  private peerLastActivity: Map<string, number> = new Map();
  private signalHandler: SignalHandler;
  private events: PeerManagerEvents;
  private myId: string;
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  private maxPeers: number;

  constructor(myId: string, signalHandler: SignalHandler, events: PeerManagerEvents = {}, options: PeerManagerOptions = {}) {
    this.myId = myId;
    this.signalHandler = signalHandler;
    this.events = events;
    this.maxPeers = options.maxPeers ?? DEFAULT_MAX_PEERS;
  }

  /**
   * Check if we're at the maximum peer capacity
   */
  isAtCapacity(): boolean {
    return this.getConnectedPeerIds().length >= this.maxPeers;
  }

  /**
   * Get the maximum number of peers allowed
   */
  getMaxPeers(): number {
    return this.maxPeers;
  }

  /**
   * Start keepalive ping/pong mechanism
   * Call this after PeerManager is set up
   */
  startKeepalive(): void {
    if (this.keepaliveInterval) return;

    this.keepaliveInterval = setInterval(() => {
      this.sendKeepalivePings();
      this.checkDeadPeers();
    }, KEEPALIVE_INTERVAL_MS);
  }

  /**
   * Stop keepalive mechanism
   */
  stopKeepalive(): void {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
  }

  /**
   * Send ping to all connected peers
   */
  private sendKeepalivePings(): void {
    const now = Date.now();
    for (const [peerId, pc] of this.peers) {
      if (pc.state === 'connected') {
        const sent = pc.send(KEEPALIVE_CHANNEL, JSON.stringify({ type: 'ping', timestamp: now }));
        if (sent) {
          console.log(`[Keepalive] Ping sent to ${peerId.slice(0, 8)}`);
        }
      }
    }
  }

  /**
   * Check for dead peers (no activity within timeout)
   */
  private checkDeadPeers(): void {
    const now = Date.now();
    const deadPeers: string[] = [];

    for (const [peerId, pc] of this.peers) {
      if (pc.state === 'connected') {
        const lastActivity = this.peerLastActivity.get(peerId) || 0;
        if (now - lastActivity > KEEPALIVE_TIMEOUT_MS) {
          console.log(`[Keepalive] Peer ${peerId.slice(0, 8)} is dead (no activity for ${Math.round((now - lastActivity) / 1000)}s)`);
          deadPeers.push(peerId);
        }
      }
    }

    // Disconnect dead peers
    for (const peerId of deadPeers) {
      this.disconnect(peerId);
    }
  }

  /**
   * Handle keepalive message (ping/pong)
   */
  handleKeepalive(fromPeer: string, data: string): void {
    try {
      const msg = JSON.parse(data);
      this.peerLastActivity.set(fromPeer, Date.now());

      if (msg.type === 'ping') {
        // Respond with pong
        const pc = this.peers.get(fromPeer);
        if (pc && pc.state === 'connected') {
          pc.send(KEEPALIVE_CHANNEL, JSON.stringify({ type: 'pong', timestamp: msg.timestamp }));
        }
      } else if (msg.type === 'pong') {
        const latency = Date.now() - msg.timestamp;
        console.log(`[Keepalive] Pong from ${fromPeer.slice(0, 8)} (latency: ${latency}ms)`);
      }
    } catch (e) {
      console.warn('[Keepalive] Invalid message:', e);
    }
  }

  /**
   * Update last activity time for a peer (call on any message received)
   */
  touchPeer(peerId: string): void {
    this.peerLastActivity.set(peerId, Date.now());
  }

  /**
   * Get all peer IDs
   */
  getPeerIds(): string[] {
    return Array.from(this.peers.keys());
  }

  /**
   * Get connected peer IDs
   */
  getConnectedPeerIds(): string[] {
    return Array.from(this.peers.entries())
      .filter(([_, pc]) => pc.state === 'connected')
      .map(([id]) => id);
  }

  /**
   * Get a peer connection
   */
  getPeer(peerId: string): PeerConnection | undefined {
    return this.peers.get(peerId);
  }

  /**
   * Initiate connection to a peer
   * Returns false if at capacity or already connected
   */
  async connect(peerId: string): Promise<boolean> {
    if (this.peers.has(peerId)) {
      return false; // Already connecting/connected
    }

    // Check capacity before initiating new connection
    if (this.isAtCapacity()) {
      console.log(`[PeerManager] At capacity (${this.maxPeers} peers), rejecting connection to ${peerId.slice(0, 8)}`);
      return false;
    }

    const pc = this.createPeerConnection(peerId);

    // Create sync channel (for DAG sync)
    pc.createDataChannel({ label: 'sync', ordered: true });

    // Create offer
    const offer = await pc.createOffer();
    this.signalHandler.sendOffer(peerId, JSON.stringify(offer));
    return true;
  }

  /**
   * Handle incoming offer
   * Implements "polite peer" pattern to handle glare (simultaneous offers)
   */
  async handleOffer(fromPeerId: string, sdp: string): Promise<void> {
    let pc = this.peers.get(fromPeerId);
    const parsed = JSON.parse(sdp);
    // Ensure type is set (browser toJSON may omit it)
    const offer = { type: 'offer' as const, ...parsed };

    // If already connected, ignore stale offers
    if (pc && pc.state === 'connected') {
      console.log(`[PeerManager] Ignoring stale offer from ${fromPeerId.slice(0, 8)}, already connected`);
      return;
    }

    // Check capacity for new connections (but allow existing peers to reconnect)
    if (!pc && this.isAtCapacity()) {
      console.log(`[PeerManager] At capacity (${this.maxPeers} peers), rejecting offer from ${fromPeerId.slice(0, 8)}`);
      return;
    }

    // Handle glare: if we have a pending local offer (have-local-offer state)
    if (pc && pc.signalingState === 'have-local-offer') {
      // Deterministic tiebreaker: lower peer ID is "polite" and yields
      const weArePolite = this.myId < fromPeerId;
      if (weArePolite) {
        // We yield: close our connection and accept theirs
        // Create fresh connection to ensure clean ICE role assignment
        console.log(`[PeerManager] Glare with ${fromPeerId.slice(0, 8)}, we yield (closing old connection)`);
        pc.close();
        this.peers.delete(fromPeerId);
        // Create fresh connection - as answerer, we'll be "controlled" role
        pc = this.createPeerConnection(fromPeerId);
        // Small delay to ensure clean state before answering
        await new Promise(resolve => setTimeout(resolve, 10));
        const answer = await pc.createAnswer(offer);
        this.signalHandler.sendAnswer(fromPeerId, JSON.stringify(answer));
        return;
      } else {
        // They should yield: ignore their offer, they'll accept our answer
        console.log(`[PeerManager] Glare with ${fromPeerId.slice(0, 8)}, they should yield (ignoring their offer)`);
        return;
      }
    }

    // No existing connection or connection in a state where we can accept offer
    if (!pc) {
      pc = this.createPeerConnection(fromPeerId);
    }

    const answer = await pc.createAnswer(offer);
    this.signalHandler.sendAnswer(fromPeerId, JSON.stringify(answer));
  }

  /**
   * Handle incoming answer
   */
  async handleAnswer(fromPeerId: string, sdp: string): Promise<void> {
    const pc = this.peers.get(fromPeerId);
    if (!pc) {
      console.warn(`[PeerManager] No connection for peer ${fromPeerId.slice(0, 8)}`);
      return;
    }

    // Check signaling state - we can only accept an answer if we have a pending offer
    if (pc.signalingState !== 'have-local-offer') {
      console.log(`[PeerManager] Ignoring answer from ${fromPeerId.slice(0, 8)} - signaling state is ${pc.signalingState}, not have-local-offer`);
      return;
    }

    const parsed = JSON.parse(sdp);
    // Ensure type is set (browser toJSON may omit it)
    const answer = { type: 'answer' as const, ...parsed };
    await pc.setRemoteAnswer(answer);
  }

  /**
   * Handle incoming ICE candidate
   */
  async handleIceCandidate(
    fromPeerId: string,
    candidate: string,
    sdpMid?: string,
    sdpMLineIndex?: number
  ): Promise<void> {
    const pc = this.peers.get(fromPeerId);
    if (!pc) {
      console.warn(`No connection for peer ${fromPeerId}`);
      return;
    }

    await pc.addIceCandidate({
      candidate,
      sdpMid: sdpMid ?? null,
      sdpMLineIndex: sdpMLineIndex ?? null,
    });
  }

  /**
   * Disconnect from a peer
   */
  disconnect(peerId: string): void {
    const pc = this.peers.get(peerId);
    if (pc) {
      pc.close();
      this.peers.delete(peerId);
      this.peerLastActivity.delete(peerId);
    }
  }

  /**
   * Disconnect from all peers
   */
  disconnectAll(): void {
    this.stopKeepalive();
    for (const pc of this.peers.values()) {
      pc.close();
    }
    this.peers.clear();
    this.peerLastActivity.clear();
  }

  /**
   * Broadcast message to all connected peers
   */
  broadcast(channel: string, data: string | ArrayBuffer): void {
    for (const pc of this.peers.values()) {
      if (pc.state === 'connected') {
        pc.send(channel, data);
      }
    }
  }

  /**
   * Send to a specific peer
   */
  send(peerId: string, channel: string, data: string | ArrayBuffer): boolean {
    const pc = this.peers.get(peerId);
    if (pc) {
      return pc.send(channel, data);
    }
    return false;
  }

  /**
   * Send a call signal (SDP offer/answer or ICE candidate) to a peer
   */
  sendCallSignal(peerId: string, signal: CallSignal): boolean {
    return this.send(peerId, CALL_CHANNEL, JSON.stringify(signal));
  }

  /**
   * Handle incoming call signal
   */
  handleCallSignal(fromPeer: string, data: string): void {
    try {
      const signal: CallSignal = JSON.parse(data);
      this.events.onCallSignal?.(fromPeer, signal);
    } catch (e) {
      console.warn('[Call] Invalid call signal:', e);
    }
  }

  /**
   * Get stats for all peers
   */
  getStats(): PeerStats[] {
    return Array.from(this.peers.entries()).map(([peerId, pc]) => ({
      peerId,
      state: pc.state,
      channels: [], // Would need to track this
      bytesReceived: 0, // Would need to track this
      bytesSent: 0,
    }));
  }

  // ============ Signal Relay ============

  /**
   * Request a connected peer to relay a signal for us
   * Used when we can't directly reach the target peer
   */
  relaySignal(viaPeer: string, signal: Omit<RelaySignal, 'hops'>): boolean {
    const relayMsg: RelaySignal = { ...signal, hops: 0 };
    return this.send(viaPeer, RELAY_CHANNEL, JSON.stringify(relayMsg));
  }

  /**
   * Handle incoming relay signal
   * If we're the target, process it. Otherwise, forward it.
   */
  handleRelaySignal(fromPeer: string, data: string): void {
    let signal: RelaySignal;
    try {
      signal = JSON.parse(data);
    } catch {
      console.warn('[Relay] Invalid relay signal');
      return;
    }

    // Prevent infinite loops
    if (signal.hops >= MAX_RELAY_HOPS) {
      console.warn('[Relay] Max hops exceeded, dropping signal');
      return;
    }

    // If we're the target, process the signal
    if (signal.to === this.myId) {
      this.processRelayedSignal(signal);
      return;
    }

    // If we're connected to the target, forward directly
    const targetPeer = this.peers.get(signal.to);
    if (targetPeer && targetPeer.state === 'connected') {
      const forwarded: RelaySignal = { ...signal, hops: signal.hops + 1 };
      targetPeer.send(RELAY_CHANNEL, JSON.stringify(forwarded));
      console.log(`[Relay] Forwarded ${signal.type} from ${signal.from.slice(0, 8)} to ${signal.to.slice(0, 8)}`);
      return;
    }

    // Otherwise, broadcast to all connected peers (gossip)
    const forwarded: RelaySignal = { ...signal, hops: signal.hops + 1 };
    const msg = JSON.stringify(forwarded);
    for (const [peerId, pc] of this.peers) {
      // Don't send back to sender
      if (peerId !== fromPeer && pc.state === 'connected') {
        pc.send(RELAY_CHANNEL, msg);
      }
    }
  }

  /**
   * Process a relayed signal that's addressed to us
   */
  private async processRelayedSignal(signal: RelaySignal): Promise<void> {
    console.log(`[Relay] Received ${signal.type} from ${signal.from.slice(0, 8)}`);

    switch (signal.type) {
      case 'relay-offer':
        await this.handleOffer(signal.from, signal.payload);
        break;
      case 'relay-answer':
        await this.handleAnswer(signal.from, signal.payload);
        break;
      case 'relay-ice':
        const ice = JSON.parse(signal.payload);
        await this.handleIceCandidate(signal.from, ice.candidate, ice.sdpMid, ice.sdpMLineIndex);
        break;
    }

    // Notify event handler
    this.events.onRelayRequest?.(signal.from, signal);
  }

  /**
   * Send offer via relay (when direct signaling isn't available)
   */
  sendOfferViaRelay(toPeer: string, sdp: string): boolean {
    const connected = this.getConnectedPeerIds();
    if (connected.length === 0) return false;

    // Send via first connected peer
    return this.relaySignal(connected[0], {
      type: 'relay-offer',
      from: this.myId,
      to: toPeer,
      payload: sdp,
    });
  }

  /**
   * Send answer via relay
   */
  sendAnswerViaRelay(toPeer: string, sdp: string): boolean {
    const connected = this.getConnectedPeerIds();
    if (connected.length === 0) return false;

    return this.relaySignal(connected[0], {
      type: 'relay-answer',
      from: this.myId,
      to: toPeer,
      payload: sdp,
    });
  }

  /**
   * Send ICE candidate via relay
   */
  sendIceCandidateViaRelay(toPeer: string, candidate: RTCIceCandidate): boolean {
    const connected = this.getConnectedPeerIds();
    if (connected.length === 0) return false;

    return this.relaySignal(connected[0], {
      type: 'relay-ice',
      from: this.myId,
      to: toPeer,
      payload: JSON.stringify({
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMLineIndex,
      }),
    });
  }

  private createPeerConnection(peerId: string): PeerConnection {
    const events: PeerConnectionEvents = {
      onStateChange: (state) => {
        if (state === 'connected') {
          // Initialize activity tracking on connect
          this.peerLastActivity.set(peerId, Date.now());
          this.events.onPeerConnected?.(peerId);
        } else if (state === 'disconnected' || state === 'failed' || state === 'closed') {
          this.peerLastActivity.delete(peerId);
          this.events.onPeerDisconnected?.(peerId);
        }
      },
      onIceCandidate: (candidate) => {
        this.signalHandler.sendIceCandidate(peerId, candidate);
      },
      onChannelOpen: (channel) => {
        this.events.onChannelOpen?.(peerId, channel);
      },
      onMessage: (channel, data) => {
        this.events.onMessage?.(peerId, channel, data);
      },
    };

    const pc = new PeerConnection(peerId, undefined, events);
    this.peers.set(peerId, pc);
    return pc;
  }
}
